import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import {
  AppMetaDataItemNames,
  CheckRideAISummaryEntity,
  PlanOfActionEntity
} from '../../../../meta-data/app-meta-data.service';
import { PlanOfActionRepositoryService } from '../../services/plan-of-action-repository.service';

@DwComponent({
  key: 'post-exam-assessment',
  name: 'Post Exam Assessment',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.PlanOfAction
})
@Component({
  selector: 'app-post-exam-assessment',
  templateUrl: './post-exam-assessment.component.html',
  styleUrls: ['./post-exam-assessment.component.scss']
})
export class PostExamAssessmentComponent extends DwSectionBaseComponent implements OnInit, OnDestroy {
  assessment: CheckRideAISummaryEntity;
  loading = true;
  showFullSummary = false;

  private formApi: DwMetaDataFormApi;
  private planOfActionId: number;
  private assessmentSubscriptions: Subscription[] = [];
  private printAncestors: HTMLElement[] = [];

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwMetaDataFormStateService: DwMetaDataFormStateService,
    private planOfActionRepositoryService: PlanOfActionRepositoryService,
    private elementRef: ElementRef<HTMLElement>
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = dwMetaDataFormStateService.state.formApi;
  }

  ngOnInit(): void {
    this.assessmentSubscriptions.push(
      this.formApi.getFormDataChanges(100).subscribe(() => this.loadAssessment())
    );

    if (this.formApi.isDataLoadComplete.value) {
      this.loadAssessment();
    }
  }

  ngOnDestroy(): void {
    this.restoreAfterPrint();
    this.assessmentSubscriptions.forEach(subscription => subscription.unsubscribe());
  }

  print(): void {
    if (!this.loading && this.assessment) {
      window.print();
    }
  }

  @HostListener('window:beforeprint')
  prepareForPrint(): void {
    if (this.loading || !this.assessment) {
      return;
    }

    this.restoreAfterPrint();
    let ancestor = this.elementRef.nativeElement.parentElement;
    while (ancestor) {
      ancestor.classList.add('post-exam-assessment-print-ancestor');
      this.printAncestors.push(ancestor);
      ancestor = ancestor.parentElement;
    }
    document.body.classList.add('post-exam-assessment-printing');
  }

  @HostListener('window:afterprint')
  restoreAfterPrint(): void {
    document.body.classList.remove('post-exam-assessment-printing');
    this.printAncestors.forEach(ancestor => ancestor.classList.remove('post-exam-assessment-print-ancestor'));
    this.printAncestors = [];
  }

  toggleFullSummary(): void {
    this.showFullSummary = !this.showFullSummary;
    this.updateSummaryContext();
  }

  private updateSummaryContext(): void {
    // Assessment-only forms do not necessarily configure a variable context.
    if (this.formApi.getVariableContext()) {
      this.formApi.patchVariableContext({ ShowFullSummary: this.showFullSummary });
    }
  }

  private loadAssessment(): void {
    if (!this.formApi.isDataLoadComplete.value) {
      return;
    }

    const planOfAction = this.formApi.getFormData<PlanOfActionEntity>();
    if (!planOfAction?.PlanOfActionId || planOfAction.PlanOfActionId === this.planOfActionId) {
      return;
    }

    this.planOfActionId = planOfAction.PlanOfActionId;
    this.loading = true;
    this.showFullSummary = false;
    this.updateSummaryContext();
    this.assessmentSubscriptions.push(
      this.planOfActionRepositoryService.loadPostExamAssessment(this.planOfActionId)
        .subscribe(assessment => {
          this.assessment = assessment;
          this.loading = false;
        }, () => {
          this.assessment = null;
          this.loading = false;
        })
    );
  }
}
